import type { OverviewSection } from '@assessments/shared'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { ProgressBar } from '@/components/ui/progress-bar'
import { cn } from '@/lib/utils'
import { OpenRowButton } from './OpenRowButton.js'
import { SECTION_STATE_LABEL, SECTION_STATE_TONE } from './sectionState.js'

export interface SectionRowProps {
  section: OverviewSection
  /** Whether the viewer may edit/open the section (Org Admin). Org View Only → false. */
  canEdit: boolean
  onOpen?: (sectionId: string) => void
}

export function SectionRow({ section, canEdit, onOpen }: SectionRowProps) {
  const { id, title, state, answeredCount, questionCount, lock, enterable, gatedByPrerequisite } =
    section

  // Org View Only opens the section too — read-only there, and the row still says so.
  const openable = enterable && onOpen !== undefined

  return (
    <Card
      onClick={openable ? () => onOpen?.(id) : undefined}
      className={cn(
        'flex flex-col gap-4 px-6 py-5 lg:flex-row lg:items-center',
        gatedByPrerequisite && 'opacity-60',
        openable && 'cursor-pointer hover:border-strong',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-jakarta text-[17px] font-bold text-heading">{title}</span>
          <Badge tone={SECTION_STATE_TONE[state]}>{SECTION_STATE_LABEL[state]}</Badge>
          {lock && (
            <span className="text-[13px] whitespace-nowrap text-subtle" title="Locked">
              {lock.heldByCurrentUser ? 'You are editing' : 'Being edited'}
            </span>
          )}
        </div>
        {gatedByPrerequisite && (
          <div className="mt-1.5 text-[15px] text-body">Complete the prerequisites to begin</div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-5">
        <ProgressBar
          answered={answeredCount}
          total={questionCount}
          label={`${title} progress`}
          tone="success"
          align="right"
          className="w-[180px]"
        />

        {!canEdit && <span className="text-[13px] font-medium text-subtle">View only</span>}
        <OpenRowButton
          label={canEdit ? 'Open' : 'View'}
          enabled={openable}
          onClick={() => onOpen?.(id)}
        />
      </div>
    </Card>
  )
}
